// 语音转文字: my own browser transcribes my own mic, and the office hands each final line to whoever hears me (people
// as captions and lines in the chat log, agents as if I'd typed it, marked as voice; office/stt.ts). Agents never get
// audio.
//
//   published mic (after 降噪 / gate / 按住说话; muted = silence) ─ stt-tap.js (16 kHz PCM16, 100 ms) ─ /stt/realtime
//   (routes/stt.ts relays to DashScope, model qwen-audio-3.1-asr-flash-streaming) ─ finals: {t:"stt", final: true};
//   partials (the sentence so far): my own captions, and in a 会议室 {t:"stt", final: false} too, at most one per
//   PART_MS, so the others in the room read along live (the office passes them only to them)
//
// The DashScope protocol (duplex streaming ASR): a text frame {header: {action: "run-task", task_id, streaming:
// "duplex"}, payload: {task_group: "audio", task: "asr", function: "recognition", model, parameters, input: {}}};
// wait for header.event "task-started"; then the audio as raw binary frames (PCM16, 16 kHz, mono; 3200 bytes per
// 100 ms); events "result-generated" (payload.output.sentence: {text, sentence_end, heartbeat?}: the whole sentence
// so far, replaced each time, final when sentence_end), "task-finished", "task-failed" (header.error_code /
// error_message). To end: {header: {action: "finish-task", task_id, streaming: "duplex"}, payload: {input: {}}} and
// wait for task-finished. One task per socket: a new stream is a new socket and a new task_id.
//
// 热词 (artin's Lark teardown, point 4): each stream's run-task carries `parameters.vocabulary` ({词: weight}, DashScope's
// instant hotwords for this model): the names of the people I hear and of the people in my room, the Raft channels
// bound to rooms, and our product names (Deck, Stamp, Holt, Raft, ...), from o.hotwords(). DashScope supports them only
// in a primary workspace: if a stream with hotwords fails or closes before it starts, the page drops them for a day
// (HOT_OFF_KEY) and reconnects at once without them, so captions never stop over it.
//
// When: 「自动」 (default) only while the office says someone needs it ({t:"stt_need"}: an agent hears me, I'm in a
// 会议室 with someone else, or someone in a meeting room with captions on hears me); 「总是」; or 「关」. Never with
// voice off or muted (back on unmute). While it runs, everyone sees the 转写中 icon (a speech bubble with a yellow mic,
// 'transcribing') on my name tag ({t:"stt_on"}), and it stays
// until a stopped stream's last words are back (the office takes lines only while it's on).
// Cost: silence isn't sent (the gate already took out non-speech): a chunk goes out only while it's loud (above the
// floor: a fixed minimum, or a few times the room's noise as measured, for a mic without 降噪), plus a short
// tail (so the server VAD can end the sentence) and a little pre-roll; the socket opens on the first loud chunk and
// shuts after a few idle minutes. The relay cuts a stream at 30 min, so a long one is replaced in a quiet moment
// before that.
import { keepRunning } from '/denoise.js';
import { t, onLang } from '/i18n.js';
import { qwenTranscriber } from '/stt-qwen.js';
const OUT_RATE = 16000, CHUNK = 1600;
const SILENT = 0.004;            // a chunk's peak below this is silence (gate closed, muted, 按住说话 released)
const NOISE_X = 3;               // ...and below this many times the noise floor (降噪 off, raw mic: room noise passes the gate)
const FLOOR_DOWN = 0.3, FLOOR_UP = 1.005; // the floor follows a quieter chunk at once-ish, a louder one slowly (~x1.6 in 10 s)
const TAIL_MS = 1200, PRE_CHUNKS = 3; // after speech keep sending this long; before it, the last 300 ms
const QUEUE_MAX = 60;            // chunks held while the socket connects (6 s)
const IDLE_CLOSE_MS = 5 * 60_000; // no speech this long: close the socket (it opens again on the next word)
const ROTATE_MS = 28 * 60_000, ROTATE_HARD_MS = 29.5 * 60_000; // the relay cuts at 30 min
const BACKOFF_MIN = 1000, BACKOFF_MAX = 60_000;
const LINE_MAX = 200;            // what the office keeps of a line (code points); longer finals go as several
const HOT_MAX = 60, HOT_WEIGHT = 4, HOT_CHARS = 2400; // hotwords per stream, their weight (1..5), the JSON's size cap
const HOT_OFF_KEY = 'deck.sttHotOff', HOT_OFF_MS = 24 * 3600_000; // a failed stream with hotwords: none for a day
const PART_MS = 340;             // live partials (会议室 only): at most ~3 a second; the office drops more than 8 per 2 s
// SFU: each final line is kept (memory + localStorage, per office and person) until the office acks it
// ({t:"stt_ack", id}), and the unacked ones go again, in order, after a reconnect or a reload. A line is kept at most
// KEEP_MS: a reconnect is 3 s, a reload a few seconds, and the office puts you back on your spot only within 20 s
// (presence.ts); words a minute old belong to a conversation that has moved on, and as captions they'd only be noise.
// A held line goes only while I'm still where I said it (the same room; on the open deck within HEAR_NEAR tiles of the
// spot): back at the spawn after a long drop, it would reach people who weren't there. At most KEEP_MAX lines, each
// sent at most SEND_MAX times (a "rate" refusal doesn't count). 不转写 drops what's held. An office from before acks (a
// rolled-back server: no sttAck in "welcome") gets each line once and it's forgotten at once, as before: it can't
// dedupe, so nothing is resent to it. A transition into or between Agora grants discards unacknowledged lines,
// including persisted lines from an earlier page, because their original audience cannot be re-established.
const KEEP_MS = 60_000, KEEP_MAX = 50, SEND_MAX = 3;
const HEAR_NEAR = 5;                // tiles: the office's HEAR_RADIUS (voice.ts)
const BURST = 6, BURST_MS = 10_000; // resends at most this many per window (the office takes 8 finals per 10 s)

const pref = (k, d) => { try { return localStorage.getItem(k) ?? d; } catch { return d; } };
const setPref = (k, v) => { try { localStorage.setItem(k, v); } catch {} };
/** 'auto' | 'on' | 'off' */
export const sttMode = () => { const m = pref('deck.stt', 'auto'); return m === 'on' || m === 'off' ? m : 'auto'; };
const ccMap = () => pref('deck.ccMap', 'off') === 'on';   // 显示字幕 on the map: off unless turned on
const ccMeet = () => pref('deck.ccMeet', 'on') !== 'off'; // 会议室字幕: on unless turned off
const sttLog = () => pref('deck.sttLog', 'on') !== 'off'; // transcript lines in the chat log
const sttLang = () => { const l = pref('deck.sttLang', ''); return l === 'zh' || l === 'en' ? l : ''; };

/** Cut a line into pieces of at most LINE_MAX code points, at a sentence mark or a space when there is one. */
function pieces(text) {
  const cps = [...text], out = [];
  while (cps.length > LINE_MAX) {
    let cut = LINE_MAX;
    for (let i = LINE_MAX; i > LINE_MAX / 2; i--) if (/[。！？!?，,；; ]/.test(cps[i - 1])) { cut = i; break; }
    out.push(cps.splice(0, cut).join('').trim());
  }
  out.push(cps.join('').trim());
  return out.filter(Boolean);
}

/**
 * @param {object} o
 * @param {(m) => void} o.send  the office socket
 * @param {() => object|null} o.me
 * @param {() => object|null} o.pipeline  micPipeline() / rawMic() (denoise.js), or null while voice is off
 * @param {() => boolean} o.muted
 * @param {() => string|null} o.room  the meeting room I'm in
 * @param {(sec) => void} o.addSettingsSection
 * @param {(text) => void} o.toast
 * @param {(text) => void} o.partial  my own words so far ('' when the sentence ended: the final comes from the office)
 * @param {() => void} o.changed  my 转写中 went on or off
 * @param {string} o.office  the office (floor) this page talks to: lines kept for resending are per office and person
 * @param {() => {zone: string|null, x: number, y: number}|null} o.where  where I am (the id of the room I'm in, by my feet)
 * @param {() => string[]} [o.hotwords]  words to recognise better (names here, channel names, product names)
 * @param {object} [o.transcriber]  transcription adapter; defaults to Qwen's PCM stream
 */
/** The 转写中 icon's tooltip on someone else's name (mine is initStt().tip(): it says why). */
export let STT_TIP_OTHER = t('stt.tipOther'); // (a live binding: in the current language)
onLang(() => { STT_TIP_OTHER = t('stt.tipOther'); });
/** Why I'm being transcribed (the office's stt_need why), as words: catalog keys. */
const WHY = { agent: 'stt.why.agent', meeting: 'stt.why.meeting' };

export function initStt(o) {
  const transcriber = o.transcriber ?? qwenTranscriber;
  let need = false, why = null;     // from the office (stt_need)
  let running = false, told = null, toldCc = null;
  let lastLoud = 0, lastSpoke = 0, floor = SILENT; // floor: the room's noise as the tap hears it (peaks)
  const draining = new Set();       // stopped streams whose last words are still coming back
  let tap = null, tapFor = null;    // the tap ({t, node, sink, own}) and the pipeline it's on
  let cur = null;                   // {ws, task, ready, queue, born, closing}
  const pre = [];                   // the last few quiet chunks, sent ahead of speech
  let backoff = BACKOFF_MIN, nextTry = 0, fails = 0, warned = false, hotOffAt = 0;
  let partAt = 0, partSent = '', partNext = null, partTimer = 0; // live partials to the office (会议室 only)
  // ---- final lines until acked: [{id, text, at, tries, conn}] (conn: the connection it was last sent on; 0: not yet)
  let lines = [], conn = 0, up = false, acks = false, qKey = null, stopped = false, pumpTimer = 0, discardOnWelcome = false;
  const sentAt = [];
  const save = () => { if (qKey && !stopped) try { if (lines.length) localStorage.setItem(qKey, JSON.stringify(lines.map(({ id, text, at, tries, zone, x, y }) => ({ id, text, at, tries, zone, x, y })))); else localStorage.removeItem(qKey); } catch {} };
  const qKeys = () => { try { return Object.keys(localStorage).filter((k) => k.startsWith('deck.sttq:')); } catch { return []; } };
  /** Still where line l was said (so whoever hears me now heard me then, give or take). */
  const here = (l) => { const w = o.where?.(); return !!w && w.zone === l.zone && (w.zone != null || Math.hypot(w.x - l.x, w.y - l.y) <= HEAR_NEAR); };
  const newId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  function prune() {
    const now = Date.now();
    const off = sttMode() === 'off';
    lines = lines.filter((l) => now - l.at <= KEEP_MS && (l.tries < SEND_MAX || l.conn === conn) && !(off && l.conn !== conn)).slice(-KEEP_MAX);
  }
  /** Send what's due, in order: lines not yet sent on this connection (the office acks or refuses each one). */
  function pump() {
    clearTimeout(pumpTimer); pumpTimer = 0;
    if (!up || stopped) return;
    prune();
    const now = Date.now();
    while (sentAt.length && now - sentAt[0] > BURST_MS) sentAt.shift();
    for (const l of [...lines]) {
      if (l.conn === conn) continue; // sent on this connection, waiting for its ack
      // a fresh line goes at once; a backlog (resends, lines held while offline) at most BURST per window
      const fresh = l.tries === 0 && l.heldFrom === conn && now - l.at < 2000;
      if (!fresh && !here(l)) { lines.splice(lines.indexOf(l), 1); continue; } // moved on: it'd reach other people
      if (!fresh && sentAt.length >= BURST) { pumpTimer = setTimeout(pump, BURST_MS - (now - sentAt[0]) + 50); break; }
      const m = { t: 'stt', text: l.text, final: true };
      if (acks) Object.assign(m, { id: l.id, age: Math.max(0, now - l.at) }, l.heldFrom !== conn ? { re: true } : {});
      if (o.send(m) === false) { up = false; break; } // not open after all (net.send): the next welcome sends it
      sentAt.push(now); l.tries++; l.conn = conn;
      if (!acks) lines.splice(lines.indexOf(l), 1); // an office without acks: sent once, as before
    }
    save();
  }
  function keep(text) {
    const w = o.where?.();
    lines.push({ id: newId(), text, at: Date.now(), tries: 0, conn: 0, heldFrom: up ? conn : -1, zone: w?.zone ?? null, x: w?.x ?? 0, y: w?.y ?? 0 });
    if (lines.length > KEEP_MAX) lines.shift();
    if (up) pump(); else save();
  }

  // ---- the sentence so far: mine on my page at once; to the office (for the others in my 会议室) at most one per
  // PART_MS, the newest words winning, and never after its final (which replaces it on their pages)
  function partial(text) {
    o.partial(text);
    if (!text) { partNext = null; partSent = ''; clearTimeout(partTimer); partTimer = 0; return; }
    if (!o.room()) return;
    partNext = [...text].slice(-LINE_MAX).join(''); // a long sentence: its newest words (the office keeps 200)
    if (!partTimer) partFlush();
  }
  function partFlush() {
    partTimer = 0;
    if (partNext == null || partNext === partSent) { partNext = null; return; }
    const wait = partAt + PART_MS - Date.now();
    if (wait > 0) { partTimer = setTimeout(partFlush, wait); return; }
    partAt = Date.now(); partSent = partNext; partNext = null;
    o.send({ t: 'stt', text: partSent, final: false });
  }

  // ---- the tap: on the pipeline's own AudioContext (its last node), else a context of its own on the track. The
  // pipeline can be swapped under us (app.js rebuilds it when the mic is unplugged or the default device changes):
  // tick() sees a different object from o.pipeline() and taps the new one, letting go of the old tap first.
  function untap() {
    const x = tap; tap = null; tapFor = null;
    if (!x) return;
    try { if (x.t.port) x.t.port.onmessage = null; else x.t.onaudioprocess = null; } catch {}
    try { x.node.disconnect(x.t); } catch {}
    try { x.t.disconnect(); x.sink.disconnect(); } catch {}
    if (x.own) x.own.close().catch(() => {});
  }
  async function buildTap(p) {
    untap(); tapFor = p;
    let own = null;
    try {
      let ctx = p.ctx, node = p.node;
      if (!ctx || !node) { // rawMic(): the mic track itself (muted / 按住说话 switch the track off: silence)
        ctx = own = new AudioContext(); node = ctx.createMediaStreamSource(new MediaStream([p.track]));
        keepRunning(ctx); // (an iPhone resumes it on a tap: denoise.js)
      }
      const sink = ctx.createGain(); sink.gain.value = 0; sink.connect(ctx.destination); // keeps the tap pulled, silently
      let t;
      try {
        await ctx.audioWorklet.addModule(new URL('./stt-tap.js', import.meta.url).pathname);
        t = new AudioWorkletNode(ctx, 'deck-stt-tap', { channelCount: 1, channelCountMode: 'explicit', numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1] });
        t.port.onmessage = (e) => { if (tapFor === p && !p.closed) chunk(new Int16Array(e.data.pcm), e.data.peak); };
      } catch { // no AudioWorklet: a ScriptProcessor doing the same
        t = ctx.createScriptProcessor(4096, 1, 1);
        const step = ctx.sampleRate / OUT_RATE; let acc = 0, n = 0, buf = new Int16Array(CHUNK), len = 0, peak = 0;
        t.onaudioprocess = (e) => {
          if (tapFor !== p || p.closed) return;
          const ch = e.inputBuffer.getChannelData(0);
          for (let i = 0; i < ch.length; i++) {
            acc += ch[i]; n++;
            if (n < step) continue;
            const v = Math.max(-1, Math.min(1, acc / n)); acc = 0; n -= step; if (n > 0) acc = ch[i] * n;
            if (Math.abs(v) > peak) peak = Math.abs(v);
            buf[len++] = v < 0 ? v * 0x8000 : v * 0x7fff;
            if (len === CHUNK) { chunk(buf, peak); buf = new Int16Array(CHUNK); len = 0; peak = 0; }
          }
        };
      }
      if (tapFor !== p || p.closed) { try { t.disconnect(); sink.disconnect(); } catch {} own?.close().catch(() => {}); return; } // swapped or revoked meanwhile
      node.connect(t); t.connect(sink);
      tap = { t, node, sink, own };
    } catch (e) { own?.close().catch(() => {}); console.warn('stt: no audio tap', e); }
  }

  // ---- audio in: 100 ms of 16 kHz PCM16 at a time
  function chunk(pcm, peak) {
    if (!running) return;
    floor = peak < floor ? floor + (peak - floor) * FLOOR_DOWN : Math.min(1, floor * FLOOR_UP);
    const now = Date.now(), loud = peak > Math.max(SILENT, floor * NOISE_X);
    if (loud) lastLoud = lastSpoke = now;
    if (!loud && now - lastLoud > TAIL_MS) { pre.push(pcm); if (pre.length > PRE_CHUNKS) pre.shift(); return; }
    if (!cur) connect();
    if (!cur) { pre.push(pcm); if (pre.length > PRE_CHUNKS) pre.shift(); return; } // backing off
    while (pre.length) put(pre.shift());
    put(pcm);
  }
  function put(pcm) { // a binary frame of PCM16 (the tap's buffer is ours: it was transferred)
    if (cur.ready && cur.transport.write(pcm.buffer)) return;
    else { cur.queue.push(pcm.buffer); if (cur.queue.length > QUEUE_MAX) cur.queue.shift(); }
  }

  // ---- the transcription provider. Transport framing and result parsing belong to the adapter.
  function connect() {
    const now = Date.now();
    if (cur || now < nextTry) return;
    const c = (cur = { transport: null, ready: false, queue: [], born: now, closing: false });
    const event = (ev) => {
      if (c.aborted) return; // a late provider callback must not revive a retired authorization scope
      if (ev.type === 'opened') c.hot = ev.hotwords === true;
      else if (ev.type === 'ready') { // audio may flow now: what was held while connecting first
        if (c.ready || c.closing) return;
        c.ready = true;
        for (const m of c.queue.splice(0)) c.transport.write(m);
      } else if (ev.type === 'partial' || ev.type === 'final') {
        backoff = BACKOFF_MIN; fails = 0; warned = false; // it works: words came back (not just a started task)
        const text = String(ev.text ?? '').trim();
        // the whole sentence so far (a stream being finished only while no newer one runs, so they don't fight)
        if (ev.type === 'partial') { if (c === cur || !cur) partial(text); return; }
        partial('');
        if (text) for (const t of pieces(text)) keep(t);
      } else if (ev.type === 'failed') {
        c.failed = true; console.warn('stt:', ev.code, ev.message);
        // with hotwords, before it ever started: most likely the hotwords (a sub-workspace has none); drop them
        if (c.hot && !c.ready) { c.hotFail = true; try { localStorage.setItem(HOT_OFF_KEY, String(Date.now())); } catch {} hotOffAt = Date.now(); }
      } else if (ev.type === 'closed') {
        if (cur === c) cur = null;
        if (draining.delete(c)) tick();
        if (c.closing) return;
        partial('');
        // with hotwords and closed before it ever started (task-failed, or DashScope just closing on a parameter it
        // won't take): again at once, without them for a day. Costs at most one extra connection a day.
        if (c.hot && !c.ready && !c.hotFail) { c.hotFail = true; try { localStorage.setItem(HOT_OFF_KEY, String(Date.now())); } catch {} hotOffAt = Date.now(); }
        if (c.hotFail) { nextTry = 0; return; }
        // dropped (not by us): try again on the next word, after a backoff that grows while it doesn't work: never got
        // going, or failed after starting (DashScope's task-failed: quota, arrears), so a dead account isn't retried
        // every second while someone talks (Imprint's review of #61). Words coming back reset it.
        if (!c.ready || c.failed) { fails++; backoff = Math.min(BACKOFF_MAX, backoff * 2); }
        nextTry = Date.now() + (c.ready && !c.failed && ev.code === 4008 ? 0 : backoff);
        if (fails >= 3 && !warned && running) { warned = true; o.toast(t('stt.down')); }
      }
    };
    try { c.transport = transcriber.open({ sampleRate: OUT_RATE, language: sttLang(), vocabulary: hotwords, office: o.office, event }); }
    catch (e) { if (cur === c) cur = null; nextTry = now + backoff; console.warn('stt: could not open provider', e); }
  }
  /** This stream's hotwords ({词: weight}), or null: none given, or turned off after a failure in the last day. */
  function hotwords() {
    if (!o.hotwords) return null;
    if (!hotOffAt) try { hotOffAt = Number(localStorage.getItem(HOT_OFF_KEY)) || 0; } catch {}
    const off = Date.now() - hotOffAt; if (hotOffAt && off >= 0 && off < HOT_OFF_MS) return null; // (a clock set back: on again)
    const v = {}; let n = 0, size = 0;
    for (const raw of o.hotwords()) {
      const w = String(raw ?? '').replace(/（访客）$/, '').replace(/[\p{Cc}\p{Cf}"\\]/gu, '').trim();
      if (!w || [...w].length > 20 || w in v) continue;
      size += w.length + 6; if (size > HOT_CHARS || ++n > HOT_MAX) break;
      v[w] = HOT_WEIGHT;
    }
    return n ? v : null;
  }
  /** Finish a stream: the last words still come back (task-finished), then it closes. */
  function finish(c) {
    if (!c || c.closing) return;
    c.closing = true; if (cur === c) cur = null;
    draining.add(c);
    c.transport?.finish();
    setTimeout(() => { c.transport?.close(); if (draining.delete(c)) tick(); }, 5000);
  }

  // ---- on or off
  const ccWanted = () => (o.room() ? ccMeet() : ccMap());
  function wantCapture() {
    const mode = sttMode();
    if (!o.me() || mode === 'off') return false;
    if (o.muted()) return false; // muted: nothing to transcribe, so no 转写中 icon either
    return mode === 'on' || need;
  }
  function tick() {
    if (stopped) return;
    const desired = wantCapture();
    o.captureIntent?.(desired);
    const p = o.pipeline();
    if (p && tapFor !== p) buildTap(p);
    if (!p && tapFor) untap();
    const w = desired && !!p && !p.closed;
    if (w !== running) {
      running = w;
      if (!w) { finish(cur); pre.length = 0; partial(''); }
      o.changed();
    }
    const now = Date.now();
    if (cur && !cur.closing) {
      const age = now - cur.born, quiet = now - lastLoud > TAIL_MS;
      if ((age > ROTATE_MS && quiet) || age > ROTATE_HARD_MS || (quiet && now - lastSpoke > IDLE_CLOSE_MS)) finish(cur);
    }
    if (o.me()) {
      const on = running || draining.size > 0; // off only once a stopped stream's last words are in
      if (told !== on) { told = on; o.send({ t: 'stt_on', on }); }
      const cc = ccWanted();
      if (toldCc !== cc) { toldCc = cc; o.send({ t: 'cc', on: cc }); }
    }
  }
  setInterval(tick, 500);

  /** why I'm being transcribed, in words (the settings' status line, the icon's tooltip on my name) */
  const reason = () => t(sttMode() === 'on' ? 'stt.why.always' : WHY[why] ?? 'stt.why.cc');

  // ---- 设置 → 语音转文字
  const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
  o.addSettingsSection?.({
    id: 'stt', title: () => t('stt.title'), order: 12,
    render(box) {
      box.className += ' voice-set';
      const status = el('div', 'hint');
      const radio = (v, text, hint) => {
        const l = el('label'), r = el('input'); r.type = 'radio'; r.name = 'sttMode'; r.value = v; r.checked = sttMode() === v;
        r.onchange = () => { setPref('deck.stt', v); r.blur(); tick(); show(); if (v === 'off') { prune(); save(); } }; // 不转写: held lines go now
        l.append(r, ' ' + text); box.append(l); if (hint) box.append(el('div', 'hint', hint));
      };
      const check = (key, on, text, hint) => {
        const l = el('label'), c = el('input'); c.type = 'checkbox'; c.checked = on();
        c.onchange = () => { setPref(key, c.checked ? 'on' : 'off'); c.blur(); tick(); };
        l.append(c, ' ' + text); box.append(l); if (hint) box.append(el('div', 'hint', hint));
      };
      const show = () => {
        status.textContent = o.available?.() === false ? t('app.voice.sttUnavailable') : running ? t('stt.status.on', { why: reason() })
          : t(sttMode() === 'off' ? 'stt.status.off' : !o.pipeline() ? 'stt.status.noVoice' : o.muted() ? 'stt.status.muted' : 'stt.status.idle');
      };
      radio('auto', t('stt.mode.auto'), t('stt.mode.autoHint'));
      radio('on', t('stt.mode.on'));
      radio('off', t('stt.mode.off'), t('stt.mode.offHint'));
      box.append(status); show();
      box.append(el('hr'));
      check('deck.ccMeet', ccMeet, t('stt.ccMeet'), t('stt.ccMeetHint'));
      check('deck.ccMap', ccMap, t('stt.ccMap'), t('stt.ccMapHint'));
      check('deck.sttLog', sttLog, t('stt.log'));
      const l = el('label'), s = el('select');
      for (const [v, k] of [['', 'stt.lang.auto'], ['zh', 'stt.lang.zh'], ['en', 'stt.lang.en']]) { const op = el('option', '', t(k)); op.value = v; op.selected = sttLang() === v; s.append(op); }
      s.onchange = () => { setPref('deck.sttLang', s.value); finish(cur); s.blur(); }; // the next stream starts with it
      l.append(t('stt.lang.label') + ' ', s); box.append(l);
      box.append(el('div', 'hint', t('stt.privacy')));
    },
  });

  return {
    /** {t:"stt_need"} from the office. */
    need(m) { need = !!m.on; why = m.why ?? null; tick(); },
    /** A (re)connected office was just welcomed (m: the "welcome"): it knows nothing of my 转写中 or captions; lines it
     *  hasn't acked go again. */
    welcome(m) {
      told = null; toldCc = null; need = false; why = null; tick();
      const key = m?.me?.sub && o.office ? `deck.sttq:${o.office}:${m.me.sub}` : null;
      const discard = discardOnWelcome; if (key) discardOnWelcome = false;
      if (discard) { lines = []; if (key) try { localStorage.removeItem(key); } catch {} }
      if (key !== qKey) { // the first welcome (or another person / office): lines kept by an earlier page
        qKey = key; lines = [];
        if (!discard) try { const v = key && JSON.parse(localStorage.getItem(key) || '[]'); if (Array.isArray(v)) lines = v.filter((l) => l && typeof l.id === 'string' && typeof l.text === 'string' && Number.isFinite(l.at)).map((l) => ({ id: l.id, text: l.text, at: l.at, tries: Number(l.tries) || 0, conn: 0, heldFrom: -1, zone: l.zone ?? null, x: Number(l.x) || 0, y: Number(l.y) || 0 })); } catch {}
        // other floors' / people's kept lines that are past keeping: gone (spoken words don't linger in the browser)
        for (const k of qKeys()) if (k !== key) try { const v = JSON.parse(localStorage.getItem(k) || '[]'); if (!Array.isArray(v) || !v.some((l) => Date.now() - l?.at <= KEEP_MS)) localStorage.removeItem(k); } catch { try { localStorage.removeItem(k); } catch {} }
      }
      conn++; up = true; acks = m?.sttAck === true;
      // an office without acks can't tell a resend from a new line: what may have reached one already isn't sent again
      if (!acks) lines = lines.filter((l) => l.tries === 0);
      pump();
    },
    /** The office socket closed: hold lines until the next welcome. */
    down() { up = false; clearTimeout(pumpTimer); pumpTimer = 0; },
    /** {t:"stt_ack"} / {t:"stt_err"} from the office. */
    msg(m) {
      const i = m.id == null ? -1 : lines.findIndex((l) => l.id === m.id);
      if (i < 0) { if (m.t === 'stt_err') console.warn('stt: a line was refused:', m.reason); return; }
      if (m.t === 'stt_err' && m.reason === 'rate') { lines[i].conn = 0; lines[i].tries--; clearTimeout(pumpTimer); pumpTimer = setTimeout(pump, 2000); return; } // again soon (KEEP_MS still bounds it)
      if (m.t === 'stt_err') console.warn('stt: a line was refused:', m.reason);
      lines.splice(i, 1); save();
    },
    /** Am I being transcribed right now (the 转写中 icon on my name). */
    on: () => running,
    /** The 转写中 icon's tooltip on my own name: 「正在转写你说的话（有 agent 听得到你）」 */
    tip: () => t('stt.tip', { why: reason() }),
    ccMap, ccMeet, log: sttLog,
    /** The meeting toolbar's 字幕 (the same setting as 设置 → 会议室字幕); the office hears of it on the next tick. */
    setCcMeet(on) { setPref('deck.ccMeet', on ? 'on' : 'off'); tick(); },
    /** app.js swapped the mic pipeline (a new micPipeline / rawMic): tap the new one now rather than on the next tick. */
    repipe() { tick(); },
    /** Signed out: forget every kept line (what I said doesn't stay in this browser). */
    forget() { lines = []; for (const k of qKeys()) try { localStorage.removeItem(k); } catch {} },
    /** An RTC provider/space change cannot carry capture or unacknowledged words into a different audience. */
    suspendForProviderChange() {
      if (o.me() && told === true) o.send({ t: 'stt_on', on: false });
      if (o.me() && toldCc === true) o.send({ t: 'cc', on: false });
      // A saved line's old location is not an authorization for the next voice space. Discard it on transition
      // rather than deliver it to a different audience after reconnect.
      discardOnWelcome = true; lines = []; if (qKey) try { localStorage.removeItem(qKey); } catch {}
      stopped = true; up = false; running = false; need = false; clearTimeout(pumpTimer); pumpTimer = 0;
      partial(''); pre.length = 0; untap();
      const sockets = new Set([...draining, ...(cur ? [cur] : [])]); cur = null; draining.clear();
      for (const c of sockets) {
        c.aborted = true; c.closing = true; c.queue.length = 0;
        c.transport?.abort();
      }
    },
    /** Resume after the provider's current authorization is ready. */
    resumeForProviderChange() { stopped = false; need = false; running = false; told = null; toldCc = null; },
    /** Stop everything (another tab took over). */
    stop() { running = false; finish(cur); draining.clear(); need = false; partial(''); stopped = true; up = false; clearTimeout(pumpTimer); },
  };
}
